"use client"

import Link from "next/link"
import { ArrowRight, CheckCircle2, BookOpen, Target, MonitorPlay, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/hooks/use-auth"
import { useRouter } from "next/navigation"



export function AuthenticatedHero() {
    const { user } = useAuth()
    const router = useRouter()

    const firstName = user?.full_name?.split(" ")[0] || "there"

    return (
        <section className="relative overflow-hidden pt-12 md:pt-16 lg:pt-20 pb-12 md:pb-16">
            {/* Background blobs — same palette as LandingHero */}
            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-primary/10 dark:bg-primary/5 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-primary/10 dark:bg-primary/5 rounded-full blur-[100px] pointer-events-none" />

            <div className="container px-4 sm:px-6 md:px-8 mx-auto">
                {/* Greeting */}
                <div className="mb-10 text-center animate-in fade-in slide-in-from-bottom-4 duration-700 fill-mode-forwards">
                 
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-foreground leading-[1.1]">
                        Hey, {firstName}!&nbsp;
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary/80 to-primary/60">
                            Ready to level up?
                        </span>
                    </h1>
              
                </div>

                {/* Two CTA cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-6 duration-1000 delay-200 fill-mode-forwards">

                    {/* ── Start Practicing card ──────────────────────────────── */}
                    <div className="group relative overflow-hidden rounded-3xl border border-border/60 bg-card p-8 flex flex-col gap-6 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                        onClick={() => router.push("/dashboard")}
                    >
                        {/* Glow accent */}
                        <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-primary/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                        <div className="flex items-center justify-between">
                            <div className="flex h-13 w-13 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/20">
                                <Target className="h-6 w-6" />
                            </div>
                            <ChevronRight className="h-5 w-5 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all duration-200" />
                        </div>

                        <div className="space-y-1.5">
                            <h2 className="text-2xl font-bold tracking-tight text-foreground">Start Practicing</h2>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                Jump into adaptive SAT questions, mock tests, vocab, and AI analytics — all in your personal dashboard.
                            </p>
                        </div>

                        <div className="space-y-2 text-sm text-muted-foreground">
                            {["Practice questions by skill", "Full-length mock tests", "AI performance insights"].map(item => (
                                <div key={item} className="flex items-center gap-2">
                                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>

                        <Button
                            size="lg"
                            className="w-full h-12 rounded-2xl gap-2 bg-primary hover:bg-primary/90 text-primary-foreground shadow-md shadow-primary/20 group-hover:shadow-lg group-hover:shadow-primary/30 transition-all"
                            onClick={(e) => { e.stopPropagation(); router.push("/dashboard") }}
                        >
                            Go to Dashboard <ArrowRight className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* ── Start Learning card ────────────────────────────────── */}
                    <div className="group relative overflow-hidden rounded-3xl border border-border/60 bg-card p-8 flex flex-col gap-6 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                        onClick={() => router.push("/go-to-lms")}
                    >
                        {/* Glow */}
                        <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-primary/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                        <div className="flex items-center justify-between">
                            <div className="flex h-13 w-13 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/20">
                                <BookOpen className="h-6 w-6" />
                            </div>
                            <ChevronRight className="h-5 w-5 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all duration-200" />
                        </div>

                        <div className="space-y-1.5">
                            <h2 className="text-2xl font-bold tracking-tight text-foreground">Start Learning</h2>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                Access your enrolled courses, live class recordings, and learning materials on the LMS — powered by your portal account.
                            </p>
                        </div>

                        <div className="space-y-2 text-sm text-muted-foreground">
                            {["Live & recorded classes", "Course progress tracking", "Instant SSO — no re-login"].map(item => (
                                <div key={item} className="flex items-center gap-2">
                                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>

                        <Button
                            asChild
                            size="lg"
                            variant="outline"
                            className="w-full h-12 rounded-2xl gap-2 border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all"
                        >
                            <Link href="/go-to-lms" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                                <MonitorPlay className="h-4 w-4" />
                                Open My Courses
                            </Link>
                        </Button>
                    </div>
                </div>

            </div>
        </section>
    )
}
